import React, { useState } from 'react';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import {
  DEFAULT_FILTERS,
  DriverDirectory,
  InvoiceFilterState,
  TIME_RANGE_OPTIONS,
  TimeRange,
  driverInfoFor
} from './types';

interface InvoiceFiltersProps {
  filters: InvoiceFilterState;
  onChange: (patch: Partial<InvoiceFilterState>) => void;
  onClear: () => void;
  brokers: string[];
  terminals: string[];
  drivers: string[];
  /** Known destinations, offered as suggestions while typing */
  destinations: string[];
  driverDirectory: DriverDirectory;
  isAdmin: boolean;
}

const fieldSx = { minWidth: { xs: '100%', sm: 150 }, flex: { sm: '0 1 auto' } };

const InvoiceFilters: React.FC<InvoiceFiltersProps> = ({
  filters,
  onChange,
  onClear,
  brokers,
  terminals,
  drivers,
  destinations,
  driverDirectory,
  isAdmin
}) => {
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  // Only the "More filters" fields count towards the badge; the rest are visible.
  const extraFilterCount = [
    filters.loadNumber !== DEFAULT_FILTERS.loadNumber,
    filters.terminal !== DEFAULT_FILTERS.terminal,
    filters.minAmount !== DEFAULT_FILTERS.minAmount,
    filters.maxAmount !== DEFAULT_FILTERS.maxAmount
  ].filter(Boolean).length;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5
        }}
      >
        <TextField
          size="small"
          placeholder="Invoice, order, load or destination..."
          value={filters.search}
          onChange={(e) => onChange({ search: e.target.value })}
          sx={{
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { sm: 280 },
            flex: { sm: '1 1 200px' }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />

        {/* Free text, with known destinations suggested as you type */}
        <Autocomplete
          freeSolo
          size="small"
          options={destinations}
          inputValue={filters.destination}
          onInputChange={(_, value) => onChange({ destination: value })}
          sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { sm: '0 1 220px' } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Destination"
              placeholder="Any destination"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <PlaceOutlinedIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="filter-status">Status</InputLabel>
          <Select
            labelId="filter-status"
            label="Status"
            value={filters.status}
            onChange={(e) => onChange({ status: e.target.value })}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="pay_to_driver">Pay to Driver</MenuItem>
            <MenuItem value="PAID">Paid</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="filter-time">Time Range</InputLabel>
          <Select
            labelId="filter-time"
            label="Time Range"
            value={filters.timeRange}
            onChange={(e) => onChange({ timeRange: e.target.value as TimeRange })}
            endAdornment={
              <InputAdornment position="end" sx={{ mr: 2 }}>
                <CalendarTodayIcon fontSize="small" color="action" />
              </InputAdornment>
            }
          >
            {Object.entries(TIME_RANGE_OPTIONS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isAdmin && (
          <FormControl size="small" sx={fieldSx}>
            <InputLabel id="filter-broker">Broker</InputLabel>
            <Select
              labelId="filter-broker"
              label="Broker"
              value={filters.broker}
              onChange={(e) => onChange({ broker: e.target.value })}
            >
              <MenuItem value="all">All Brokers</MenuItem>
              {brokers.map((broker) => (
                <MenuItem key={broker} value={broker}>
                  {broker}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {isAdmin && (
          <FormControl size="small" sx={fieldSx}>
            <InputLabel id="filter-driver">Driver</InputLabel>
            <Select
              labelId="filter-driver"
              label="Driver"
              value={filters.driver}
              onChange={(e) => onChange({ driver: e.target.value })}
            >
              <MenuItem value="all">All Drivers</MenuItem>
              {drivers.map((dogtag) => (
                <MenuItem key={dogtag} value={dogtag}>
                  {driverInfoFor(driverDirectory, dogtag).name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Badge badgeContent={extraFilterCount} color="primary">
          <Button
            startIcon={<TuneIcon />}
            onClick={(e) => setMoreAnchor(e.currentTarget)}
            sx={{ color: 'text.secondary' }}
          >
            More Filters
          </Button>
        </Badge>

        <Button startIcon={<RefreshIcon />} onClick={onClear} color="primary">
          Clear
        </Button>
      </Box>

      <Popover
        open={Boolean(moreAnchor)}
        anchorEl={moreAnchor}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 300 } } }}
      >
        <Typography variant="subtitle2" gutterBottom>
          More Filters
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            size="small"
            label="Load Number"
            value={filters.loadNumber}
            onChange={(e) => onChange({ loadNumber: e.target.value })}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="filter-terminal">Terminal</InputLabel>
            <Select
              labelId="filter-terminal"
              label="Terminal"
              value={filters.terminal}
              onChange={(e) => onChange({ terminal: e.target.value })}
            >
              <MenuItem value="all">All Terminals</MenuItem>
              {terminals.map((terminal) => (
                <MenuItem key={terminal} value={terminal}>
                  {terminal}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              size="small"
              type="number"
              label="Min Amount"
              value={filters.minAmount}
              onChange={(e) => onChange({ minAmount: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Max Amount"
              value={filters.maxAmount}
              onChange={(e) => onChange({ maxAmount: e.target.value })}
              fullWidth
            />
          </Box>
          <Button
            onClick={() =>
              onChange({
                loadNumber: DEFAULT_FILTERS.loadNumber,
                terminal: DEFAULT_FILTERS.terminal,
                minAmount: DEFAULT_FILTERS.minAmount,
                maxAmount: DEFAULT_FILTERS.maxAmount
              })
            }
            disabled={extraFilterCount === 0}
          >
            Reset these filters
          </Button>
        </Box>
      </Popover>
    </Paper>
  );
};

export default InvoiceFilters;
